/**
 * Keep iOS/WebKit at the document's inline origin when the page is not zoomed
 * in. CSS removes the overflow sources; this guard corrects WebKit's retained
 * horizontal visual-viewport offset after a pinch or diagonal gesture.
 *
 * It intentionally does not cancel touch events. Pinch zoom and horizontal
 * inspection remain available while the user is zoomed in.
 */
(() => {
  const touchDevice = window.matchMedia('(hover: none) and (pointer: coarse)');
  let frameRequested = false;

  const isAtBaseScale = () =>
    !window.visualViewport || window.visualViewport.scale <= 1.01;

  const clampInlineOffset = () => {
    frameRequested = false;

    if (!isAtBaseScale()) return;

    const rootScroller = document.scrollingElement;
    const pageWrapper = document.querySelector('.page-wrapper');

    if (rootScroller && rootScroller.scrollLeft !== 0) {
      rootScroller.scrollLeft = 0;
    }

    if (pageWrapper && pageWrapper.scrollLeft !== 0) {
      pageWrapper.scrollLeft = 0;
    }

    if (window.scrollX !== 0) {
      window.scrollTo(0, rootScroller?.scrollTop ?? window.scrollY);
    }

    // Never allow the desktop drawer "squeeze" class to resize an RTL page.
    // Safari can make the 990px media query match temporarily while pinching.
    if (pageWrapper && document.documentElement.dir === 'rtl') {
      pageWrapper.classList.remove('page-wrapper--drawer-open');
    } else if (touchDevice.matches && pageWrapper) {
      pageWrapper.classList.remove('page-wrapper--drawer-open');
    }
  };

  const requestClamp = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(clampInlineOffset);
  };

  window.addEventListener('scroll', requestClamp, { passive: true });
  window.addEventListener('resize', requestClamp, { passive: true });
  window.addEventListener('pageshow', requestClamp, { passive: true });
  document.addEventListener('touchend', requestClamp, { passive: true });
  document.addEventListener('theme-drawer:open', requestClamp, { passive: true });
  document.addEventListener('theme-drawer:close', requestClamp, { passive: true });
  window.visualViewport?.addEventListener('scroll', requestClamp, { passive: true });
  window.visualViewport?.addEventListener('resize', requestClamp, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', requestClamp, { once: true });
  } else {
    requestClamp();
  }
})();

